import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import { client } from '@services/api/client'
import { AUTH_TOKEN } from '@constants/storages'
import {
  useState,
  ReactNode,
  createContext,
  useEffect,
  useCallback,
} from 'react'
import { isWeb } from 'tamagui'
import { router, usePathname, useRootNavigationState } from 'expo-router'
import { isPublic } from '@utils/routes'
import { useRudderstack } from '@hooks/useAnalytics'
import { jwtDecode } from 'jwt-decode'
import { apolloClient } from '@services/api/apollo'
import { LoadingScreen } from '@components/LoadingScreen'
import { useUser } from '@hooks/useUser'
import { IUser } from '@dtos/User'
import { useHandleWebHomeToRedirect } from '@hooks/handleWebHomeToRedirect'
import { useToastController } from '@tamagui/toast'

export interface ISessionProviderProps {
  logged: boolean
  loggedLoading: boolean
  token: string | null
  user: IUser | null
  signInByToken: (token: string) => Promise<boolean>
  logoutSession: () => void
}

interface IJWTHeader {
  sub: number
}

export const SessionContext = createContext<ISessionProviderProps>(
  {} as ISessionProviderProps,
)

const LOGIN_URL = '/users/sign_in'

export function SessionProvider({ children }: { children: ReactNode }) {
  const rootNavigationState = useRootNavigationState()
  const [logged, setLogged] = useState(false)
  const [loggedLoading, setLoggedLoading] = useState(true)
  const [user, setUser] = useState<IUser | null>(null)
  const [token, setSessionToken] = useState<string | null>(null)
  const { getUser } = useUser()
  const pathname = usePathname()
  const { getItem: getToken, removeItem: removeToken } =
    useAsyncStorage(AUTH_TOKEN)
  const { identify } = useRudderstack()
  const toast = useToastController()
  const handleWebHomeToRedirect = useHandleWebHomeToRedirect()

  const logoutSession = useCallback(() => {
    removeToken()
    setLogged(false)
    setUser(null)
    setSessionToken(null)
  }, [removeToken])

  const redirectToLogin = useCallback(() => {
    if (isWeb) window.location.href = handleWebHomeToRedirect
    else router.replace(LOGIN_URL)
  }, [handleWebHomeToRedirect])

  const signInByToken = useCallback(
    async (newToken: string) => {
      try {
        client.defaults.headers.Authorization = `Bearer ${newToken}`
        apolloClient.defaultContext.headers = {
          authorization: `Bearer ${newToken}`,
        }

        const { data, error } = await getUser()
        if (error || !data?.user) {
          if (error?.message === 'Token is invalid') {
            toast.show('Sua sessão expirou!', {
              message: 'Realize o login novamente para continuar estudando.',
              type: 'error',
            })
          }
          await removeToken()
          return false
        }

        setUser(data.user)
        setSessionToken(newToken)
        setLogged(true)

        try {
          const { sub } = jwtDecode<IJWTHeader>(newToken, { header: true })
          if (sub) {
            identify(sub)
          }
        } catch (error) {
          console.error('Erro ao decodificar token:', error)
        }

        setLoggedLoading(false)
        return true
      } catch (error) {
        console.error('Erro ao fazer login com token:', error)
        return false
      }
    },
    [getUser, identify, removeToken, toast],
  )

  const initializeSession = useCallback(async () => {
    try {
      const storedToken = await getToken()
      if (!storedToken) {
        setLoggedLoading(false)
        setLogged(false)
        return
      }

      const successSignIn = await signInByToken(storedToken)
      if (!successSignIn) {
        redirectToLogin()
      }
    } catch (error) {
      console.error('Erro ao inicializar sessão:', error)
      setLoggedLoading(false)
    }
  }, [getToken, signInByToken, redirectToLogin])

  useEffect(() => {
    initializeSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isPublic) return
    if (!rootNavigationState.key) return
    if (logged && pathname === LOGIN_URL && isWeb) {
      window.location.href = '/'
    }

    if (!logged && !loggedLoading) {
      if (pathname === LOGIN_URL) return

      redirectToLogin()
    }
  }, [
    logged,
    loggedLoading,
    rootNavigationState.key,
    pathname,
    redirectToLogin,
  ])

  if (loggedLoading) return <LoadingScreen />

  return (
    <SessionContext.Provider
      value={{
        logged,
        loggedLoading,
        token,
        signInByToken,
        user,
        logoutSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}
